import React, { useState } from 'react';

import {
  Users,
  UserCheck,
  UserX,
  UserMinus,
  CalendarDays,
  CalendarCheck,
  CalendarX,
  Percent,
} from 'lucide-react';

import Dropdown from '../../ui-components/Dropdown';
import {
  DashboardDateRange,
  useGetDashboardKpisQuery,
} from './dashboardApi';

/* ─── types ─── */
interface KpiCardProps {
  title: string;
  value: string | number;
  caption: string;
  icon: React.ReactNode;
  iconBg: string;
  captionColor?: string;
}

/* ─── static data ─── */
const DATE_RANGE_OPTIONS: Array<{ label: string; value: DashboardDateRange }> = [
  { label: 'All', value: 'all' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'Last 3 Months', value: 'last_3_months' },
  { label: 'Last 6 Months', value: 'last_6_months' },
];
const DATE_RANGE_VALUES: DashboardDateRange[] = DATE_RANGE_OPTIONS.map(
  (option) => option.value,
);

/* ─── sub-components ─── */
const KpiCard: React.FC<KpiCardProps> = ({
  title,
  value,
  caption,
  icon,
  iconBg,
  captionColor = 'text-neutral-500 dark:text-neutral-400',
}) => (
  <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-4 flex items-start justify-between gap-4 shadow-sm hover:shadow-md transition-shadow">
    <div className="flex-1 min-w-0">
      <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 mb-1">
        {title}
      </p>
      <p className="text-2xl font-bold text-neutral-900 dark:text-white">
        {value}
      </p>
      <p className={`text-xs mt-1 ${captionColor}`}>{caption}</p>
    </div>
    <div
      className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}
    >
      {icon}
    </div>
  </div>
);

/* ─── main component ─── */
const DashboardPage: React.FC = () => {
  const [dateRange, setDateRange] = useState<string | number>('all');
  const handleDateRangeChange = (
    value: string | number | Array<string | number>,
  ) => {
    if (Array.isArray(value)) return;
    setDateRange(value);
  };
  const selectedDateRange: DashboardDateRange =
    typeof dateRange === 'string' &&
    DATE_RANGE_VALUES.includes(dateRange as DashboardDateRange)
      ? (dateRange as DashboardDateRange)
      : 'all';
  const { data: kpiResponse, isLoading, isFetching } =
    useGetDashboardKpisQuery(selectedDateRange, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    });

  const kpis = kpiResponse?.data;
  const selectedDateRangeLabel =
    DATE_RANGE_OPTIONS.find((option) => option.value === selectedDateRange)
      ?.label ?? 'All';
  const isKpiLoading = isLoading || isFetching;
  const formatCount = (value?: number) =>
    isKpiLoading ? '...' : new Intl.NumberFormat().format(value ?? 0);

  const deletedUsersPercentage = Number(kpis?.deletedUsersPercentage ?? 0);
  const deletedUsersCaption = isKpiLoading
    ? '...'
    : `${deletedUsersPercentage.toFixed(1)}% / ${selectedDateRangeLabel}`;

  const repeatPercentage = Number(kpis?.repeatEventCreatorsPercentage ?? 0);
  const repeatEventCreatorsValue = isKpiLoading
    ? '...'
    : `${Math.round(repeatPercentage)}%`;

  const userKpis = [
    {
      title: 'Total Users',
      value: formatCount(kpis?.totalUsers),
      caption: 'Excluding soft-deleted users',
      icon: <Users className="w-5 h-5" />,
      iconBg: 'bg-brand-100 dark:bg-brand-900/40 text-brand-500',
      captionColor: 'text-brand-600 dark:text-brand-400',
    },
    {
      title: 'Active Users',
      value: formatCount(kpis?.activeUsers),
      caption: 'Users with active status',
      icon: <UserCheck className="w-5 h-5" />,
      iconBg: 'bg-success-100 dark:bg-success-900/40 text-success-500',
      captionColor: 'text-success-600 dark:text-success-400',
    },
    {
      title: 'Inactive Users',
      value: formatCount(kpis?.inactiveUsers),
      caption: 'Users with inactive status',
      icon: <UserX className="w-5 h-5" />,
      iconBg: 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500',
      captionColor: 'text-neutral-500 dark:text-neutral-400',
    },
    {
      title: 'Deleted Users',
      value: formatCount(kpis?.deletedUsers),
      caption: deletedUsersCaption,
      icon: <UserMinus className="w-5 h-5" />,
      iconBg: 'bg-error-100 dark:bg-error-900/40 text-error-500',
      captionColor: 'text-error-600 dark:text-error-400',
    },
  ];

  const eventKpis = [
    {
      title: 'Total Events',
      value: formatCount(kpis?.totalEvents),
      caption: 'All events',
      icon: <CalendarDays className="w-5 h-5" />,
      iconBg: 'bg-brand-100 dark:bg-brand-900/40 text-brand-500',
      captionColor: 'text-brand-600 dark:text-brand-400',
    },
    {
      title: 'Events Active (Right Now)',
      value: formatCount(kpis?.activeEventsRightNow),
      caption: 'Currently happening',
      icon: <CalendarCheck className="w-5 h-5" />,
      iconBg: 'bg-success-100 dark:bg-success-900/40 text-success-500',
      captionColor: 'text-success-600 dark:text-success-400',
    },
    {
      title: 'Events Faded (Last 7 Days)',
      value: formatCount(kpis?.eventsFadedLast7Days),
      caption: 'Auto-deleted in last week',
      icon: <CalendarX className="w-5 h-5" />,
      iconBg: 'bg-warning-100 dark:bg-warning-900/40 text-warning-500',
      captionColor: 'text-warning-600 dark:text-warning-400',
    },
    {
      title: 'Repeat Event Creators (%)',
      value: repeatEventCreatorsValue,
      caption: 'Users with 2+ events in selected range',
      icon: <Percent className="w-5 h-5" />,
      iconBg: 'bg-brand-100 dark:bg-brand-900/40 text-brand-500',
      captionColor: 'text-brand-600 dark:text-brand-400',
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/* Date Range Filter */}
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-neutral-600 dark:text-neutral-400 flex items-center gap-1.5">
          <CalendarDays className="w-4 h-4" />
          Date Range:
        </span>
        <div className="w-44">
          <Dropdown
            value={dateRange}
            setValue={handleDateRangeChange}
            options={DATE_RANGE_OPTIONS}
            placeholder="Select range"
            isSearchable={false}
            id="dashboard-date-range"
          />
        </div>
      </div>

      {/* User KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {userKpis.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} />
        ))}
      </div>

      {/* Event KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {eventKpis.map((kpi) => (
          <KpiCard key={kpi.title} {...kpi} />
        ))}
      </div>
    </div>
  );
};

export default DashboardPage;
